import React, { useEffect, useState } from 'react';

import { ErrorModal, ShowIf } from 'src/components';
import ButtonGroup, { ButtonConfig, LayoutType } from 'src/components/button-group/button-group';
import {
  ButtonParamType,
  DefaultButtonStyle,
} from 'src/components/button/button';
import './validation-fail-modal.scss';

export enum ValidationFailModalStateType {
  HIDDEN,
  FAILED,
  FAILED_ONE_TRY_LEFT,
  FAILED_MAX_TRIES_EXCEEDED,
}

type HiddenState = {
  state: ValidationFailModalStateType.HIDDEN;
};

type Failed = {
  state: ValidationFailModalStateType.FAILED;
  onEditBankDetailsButtonClick: () => void;
};

type FailedOneTryLeftState = {
  state: ValidationFailModalStateType.FAILED_ONE_TRY_LEFT;
  onEditBankDetailsButtonClick: () => void;
};

type FailedMaxTriesExceeded = {
  state: ValidationFailModalStateType.FAILED_MAX_TRIES_EXCEEDED;
  maxNumberOfTriesWord: string;
  onRestartApplication: () => void;
};

export type ValidationFailModalState =
  | HiddenState
  | Failed
  | FailedOneTryLeftState
  | FailedMaxTriesExceeded;

export const ValidationFailModal = (props: ValidationFailModalState) => {
  const [buttons, setButtons] = useState<Array<ButtonConfig>>([]);
  const { state } = props;

  const getEditButton = (handleClick: () => void): ButtonConfig => {
    return {
      automationId: 'Onboarding_Your_Details_Banking_Button_Edit_Bank_Details',

      label: 'Edit bank details',
      buttonStyle: DefaultButtonStyle()
        .hasPrimaryAction()
        .isMediumSize()
        .hasLightMode()
        .build(),
      className: 'modal-button',
      buttonParams: {
        type: ButtonParamType.NormalButton,
        onClick: handleClick,
      },
    };
  };

  const getRestartButton = (handleClick: () => void): ButtonConfig => {
    return {
      automationId: 'Onboarding_Your_Details_Banking_Button_Restart_Application',
      buttonParams: {
        type: ButtonParamType.NormalButton,
        onClick: handleClick,
      },
      label: 'Restart application',
      buttonStyle: DefaultButtonStyle()
        .hasPrimaryAction()
        .isMediumSize()
        .hasLightMode()
        .build(),
      className: 'modal-button',
    };
  };

  useEffect(() => {
    switch (props.state) {
      case ValidationFailModalStateType.FAILED:
      case ValidationFailModalStateType.FAILED_ONE_TRY_LEFT:
        setButtons([getEditButton(props.onEditBankDetailsButtonClick)]);
        break;

      case ValidationFailModalStateType.FAILED_MAX_TRIES_EXCEEDED:
        setButtons([getRestartButton(props.onRestartApplication)]);
        break;
    }
  }, [props]);

  return (
    <div className="ValidationFailModal">
      <ErrorModal
        show={state !== ValidationFailModalStateType.HIDDEN && buttons.length > 0}
        title="Unable to verify your bank details"
        body={() => (
          <div className="body-style">
            <ShowIf condition={state === ValidationFailModalStateType.FAILED_ONE_TRY_LEFT}>
              <p className="failed-body content-large-regular">
                You have <b>one</b> final attempt remaining
              </p>
            </ShowIf>

            <ShowIf condition={state === ValidationFailModalStateType.FAILED_MAX_TRIES_EXCEEDED}>
              <p className="failed-body content-large-regular">
                You have exceeded your{' '}
                <b>{(props as FailedMaxTriesExceeded).maxNumberOfTriesWord}</b> try limit.
              </p>
            </ShowIf>

            <p className="content-large-regular">
              <ShowIf
                condition={
                  state === ValidationFailModalStateType.FAILED ||
                  state === ValidationFailModalStateType.FAILED_ONE_TRY_LEFT
                }
              >
                We are unable to verify your bank details.
                <span className="is-hidden-mobile">
                  <br />
                </span>
                &nbsp;Please enter the correct banking details to continue with your order.
              </ShowIf>

              <ShowIf condition={state === ValidationFailModalStateType.FAILED_MAX_TRIES_EXCEEDED}>
                Your application will be closed, if you want you can restart with the correct
                business and bank information.
              </ShowIf>
            </p>

            <div className="bar" />
          </div>
        )}
        footer={() => <ButtonGroup buttons={buttons} layoutType={LayoutType.CENTERED} />}
      />
    </div>
  );
};
